<template>
    <div class="content">
        <div v-for="block in blocks" class="block">
            <div v-if="block.type === 'header'" :class="'render-' + block.type" :style="alignStyle(block)">
                <h1 v-if="block.data.level === 1" v-html="block.data.text"></h1>
                <h2 v-else-if="block.data.level === 2" v-html="block.data.text"></h2>
                <h3 v-else-if="block.data.level === 3" v-html="block.data.text"></h3>
                <h4 v-else-if="block.data.level === 4" v-html="block.data.text"></h4>
                <h5 v-else-if="block.data.level === 5" v-html="block.data.text"></h5>
                <h6 v-else-if="block.data.level === 6" v-html="block.data.text"></h6>
            </div>

            <div v-if="block.type === 'paragraph'" :class="'render-' + block.type" :style="alignStyle(block)">
                <p v-html="block.data.text"></p>
            </div>

            <div v-if="block.type === 'delimiter'" :class="'render-' + block.type">
                <hr/>
            </div>

            <div v-if="block.type === 'embed'" :class="'render-' + block.type">
                <iframe style="width:100%;" height="320" :src="block.data.embed" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
            </div>

            <div v-if="block.type === 'table'" :class="'render-' + block.type">
                <template v-if="block.data.withHeadings">
                    <table class="table is-striped is-bordered">
                        <tr v-for="(r, index) in block.data.content">
                            <template v-for="c in r">
                                <th v-if="index===0" v-html="c"></th>
                                <td v-else v-html="c"></td>
                            </template>
                        </tr>
                    </table>
                </template>
                <template v-else>
                    <table class="table is-striped is-bordered">
                        <tr v-for="r in block.data.content">
                            <td v-for="c in r" v-html="c"></td>
                        </tr>
                    </table>
                </template>

            </div>

            <div v-if="block.type === 'warning'" :class="'render-' + block.type" class="box">
                <div class="columns is-gapless">
                    <div class="column is-narrow mr-3">
                        <b-icon size="is-large" pack="far" icon="info-circle"/>
                    </div>
                    <div class="column is-11">
                        <h6 class="pt-3 mb-2">{{ block.data.title }}</h6>
                        <p v-html="block.data.message"></p>
                    </div>
                </div>
            </div>

            <div v-if="block.type === 'image'" :class="['render-' + block.type, {'bordered': block.data.withBorder, 'stretched': block.data.stretched, 'backgrounded': block.data.withBackground}]">
                <div class="image-container"><img :src="block.data.file.url"/></div>
                <div class="image-caption" v-if="block.data.caption" v-html="block.data.caption"></div>
            </div>

            <div v-if="block.type === 'quote'" :class="'render-' + block.type">
                <blockquote>
                    <p v-html="block.data.text"></p>
                    <p class="has-text-right is-size-6">{{ block.data.caption }}</p>
                </blockquote>
            </div>

            <div v-if="block.type === 'list'" :class="'render-' + block.type" class="content" :style="alignStyle(block)">
                <ol v-if="block.data.style === 'ordered'">
                    <ejs-list-item type="ol" :item="i" v-for="i in block.data.items"/>
                </ol>
                <ul v-else>
                    <ejs-list-item type="ul" :item="i" v-for="i in block.data.items"/>
                </ul>
            </div>

            <div v-if="block.type === 'userInput'" :class="'render-' + block.type">
                {{ block.data.title }}
                <div v-if="block.data.fieldType === 'Text Field'">
                    <b-input @blur="saveData(block)" v-model="data[block.data.id]"/>
                </div>
                <div v-if="block.data.fieldType === 'Text Area'">
                    <textarea/>
                </div>
            </div>

            <div v-if="block.type === 'componentReview'" :class="'render-' + block.type">
                <h3 class="is-size-4">
                    Completion Summary & Review
                </h3>
                <p>Below is the content you completed while taking this course. Please review the details of this content to verify it's accuracy. If a specific problem is found either make corrections or contact us for additional help.</p>

                <table class="table is-striped ">
                    <tr>
                        <th>Content</th>
                        <th>Started</th>
                        <th>Completed</th>
                        <th>Time</th>
                    </tr>
                    <tr v-for="o in enrollment.course.outline" v-if="o.type !== 'HEADER'" class="is-size-7">
                        <td>{{ o.title }}</td>
                        <td>{{ o.started_at | moment('MM/DD/YYYY') }}</td>
                        <td>{{ o.completed_at | moment('MMM Do, YYYY hh:mm a') }}</td>
                        <td>{{ o.time }}</td>
                    </tr>
                </table>
            </div>

            <div v-if="block.type === 'quizMultiple'" :class="['render-' + block.type, block.data.graded, {'reveal': block.data.reveal}]">
                <h4 class="graded" :class="block.data.graded" v-if="block.data.graded === 'correct'"><i class="fas fa-check-circle mr-2"></i>Correct</h4>
                <h4 class="graded" :class="block.data.graded" v-if="block.data.graded === 'incorrect'"><i class="fas fa-times-circle mr-2"></i>Incorrect</h4>
                <div class="question-container">
                    <h4 class="is-size-6 question"><span v-html="block.data.title"></span></h4>
                    <div class="message" v-if="block.data.message">{{block.data.message}}</div>
                    <div v-for="a in JSON.parse(block.data.answers)" class="answer">
                        <b-radio @input="saveData(block)" v-model="data[block.data.id]" :native-value="a" :disabled="!!(block.data.graded)">{{ a.answer }}</b-radio>
                    </div>
                    <div v-if="block.data.reveal" class="reveal">
                        <i class="fas fa-check-circle mr-2"></i><strong>Correct Answer(s): </strong>
                        <div v-for="a in JSON.parse(block.data.answers)" class="reveal-answer" v-if="a.correct">
                            {{ a.answer }}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
    import moment from "moment";
    import EjsListItem from "./editorjs/EjsListItem";

    export default {
        name: "RenderComponent",
        components: {EjsListItem},
        props: {
            component: {
                type: Object,
                required: true
            },
            data: {
                type: Object
            },
            enrollment: {}
        },
        data: function() {
            return {};
        },
        computed: {
            blocks: function() {
                if (this.component && this.component.content) {
                    let content = this.component.content;
                    if (typeof content === 'string') content = JSON.parse(content);
                    return content.blocks;
                } else {
                    return [];
                }
            }
        },
        methods: {
            saveData(block) {
                let payload = {
                    block: JSON.stringify(block),
                    field: block.data.id,
                    value: JSON.stringify(this.data[block.data.id]),
                    component_id: this.component.id,
                };

                this.$emit('saveData', payload);
            },

            formatTime(min) {
                return moment.utc(min).format('HH:mm:ss');
            },

            // The editor saves alignment as a block tune (alignTune); it was
            // never rendered before, so centered/right content showed left.
            alignStyle(block) {
                const alignment = block.tunes && block.tunes.alignTune && block.tunes.alignTune.alignment;
                return alignment ? {textAlign: alignment} : null;
            }
        }
    };

</script>

<style lang="scss" scoped>
    .block {
        margin-bottom: 0;

        .render-header, .render-paragraph, .render-table, .render-quote, .render-userInput {
            padding: 0 2rem;
        }

        .render-header {
            padding: 3rem 2rem 1.5rem;

            h1, h2, h3, h4, h5, h6 {
                margin: 0;
                padding-bottom: 0.4rem;
                border-bottom: 2px solid #f4f4f4;
            }
        }

        .render-paragraph {
            padding: 0 2rem 1.2rem;
        }

        .render-image {
            padding: 0;
            margin: 0 2rem 1.2rem;

            img {
                padding: 0;
            }

            &.bordered {
                border: 1px solid #e7e7e7;
                border-radius: 4px;
            }

            &.stretched {
                margin: 0 0 1.2rem;
                border-radius: 0;

            }

            &.backgrounded {
                background: #f4f4f4;

                .image-container {
                    max-width: 60%;
                    margin: 0 auto;
                    padding: 1.2rem 0;
                }
            }

            .image-caption {
                text-align: center;
                font-size: 0.8rem;
                padding: 0.3rem 4rem;
                background-color: #f4f4f4;
                font-style: italic;
            }
        }

        .render-delimiter {
            padding-bottom: 1.2rem;
        }

        .render-warning {
            margin: 2rem 1.5rem;
            padding-bottom: 2rem;
            background-color: #f1f1f1;
        }

        .render-quizMultiple {
            margin: 0 0.8rem 2rem;
            border: 1px solid #e7e7e7;
            border-radius: 0.3rem;

            .graded {
                display: none;
                padding: 0.3rem 1.2rem;
                font-size: 1rem;

                &.correct {
                    display: block;
                    background-color: green;
                    color: white;
                    padding: 0.6rem 1rem;
                }

                &.incorrect {
                    display: block;
                    background-color: #be1313;
                    color: white;
                    padding: 0.6rem 1rem;
                }
            }

            .question-container {
                padding: 1.2rem 1rem;
            }

            .question {
                margin-bottom: 1.5rem;
            }

            .answer {
                margin: 0 0.3rem;
            }

            &.reveal.incorrect {
                border-color: #be1313 !important;
            }

            &.reveal.correct {
                border-color: green !important;
            }

            .reveal {
                color: green;
                padding: 1rem 0.5rem 0;

                strong {
                    color: green;
                }
            }

            .reveal-answer {
                margin-top: 0.2rem;
            }
        }
    }
</style>

<style lang="scss">
    .render-list {

        ol, ul {
            margin-top: 0;

            li.ejs-item, li.ejs-item {
                margin-left: 1rem;
                padding-left: 0.8rem;
                margin-bottom: 0.5rem;
            }
        }

    }
</style>
